import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';
import {
  ViewerDocument,
  getFileName,
  getPreviewKind,
  toApiPath,
} from '../../utils/documentUrl';

interface DocumentViewerModalProps {
  isOpen: boolean;
  documents: ViewerDocument[];
  /** Which document to show first; defaults to the first in the list. */
  initialDocumentId?: string;
  /** Context line in the header, e.g. the candidate's name, so the user keeps their bearings. */
  subtitle?: string;
  onClose: () => void;
}

type LoadState = 'loading' | 'ready' | 'error';

/**
 * Previews candidate documents in place, so reviewing a resume never navigates away
 * from (or covers over) the application the reviewer is working through.
 *
 * The file is fetched over XHR and shown from an object URL rather than framed directly.
 * That is what makes it work in development: CRA's dev proxy will not forward a GET whose
 * Accept header includes text/html, so an <iframe> pointed at /api/... is served the app's
 * own index.html instead of the document. An object URL also gives the download and
 * open-in-new-tab actions something that works identically in both environments.
 *
 * Layered above the application modal at z-60: the application stays mounted behind it,
 * so closing returns to exactly the state it was left in.
 */
const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  isOpen,
  documents,
  initialDocumentId,
  subtitle,
  onClose,
}) => {
  const [activeId, setActiveId] = useState<string | undefined>(initialDocumentId);
  const [objectUrl, setObjectUrl] = useState<string>('');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [docxError, setDocxError] = useState<string>('');
  const docxContainerRef = useRef<HTMLDivElement | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const active = documents.find((doc) => doc.id === activeId) || documents[0];
  const activeUrl = active?.url;

  // Follow the caller's choice each time the viewer is opened for a new document.
  useEffect(() => {
    if (isOpen) setActiveId(initialDocumentId);
  }, [isOpen, initialDocumentId]);

  // Fetch the active document and expose it as an object URL.
  useEffect(() => {
    if (!isOpen || !activeUrl) return;

    let cancelled = false;
    let created = '';
    setLoadState('loading');
    setErrorMessage('');
    setObjectUrl('');
    setBlob(null);
    setDocxError('');

    axiosInstance
      .get(toApiPath(activeUrl), { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        created = URL.createObjectURL(response.data);
        setObjectUrl(created);
        setBlob(response.data);
        setLoadState('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        setErrorMessage(
          error?.response?.status === 404
            ? 'This document is not on the server. It may not have been uploaded to this environment.'
            : 'This document could not be loaded.'
        );
        setLoadState('error');
      });

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [isOpen, activeUrl]);

  // Word documents have no native browser renderer, so docx-preview lays them out into
  // page-like nodes from the blob we already hold. Imported on demand to keep the renderer
  // out of the main bundle, and done client-side so candidate CVs are never handed to a
  // third-party viewer service.
  useEffect(() => {
    if (!isOpen || loadState !== 'ready' || !blob) return;
    if (getPreviewKind(activeUrl || '') !== 'docx') return;

    const container = docxContainerRef.current;
    if (!container) return;

    let cancelled = false;
    container.innerHTML = '';

    import('docx-preview')
      .then(({ renderAsync }) => {
        if (cancelled) return undefined;
        return renderAsync(blob, container, undefined, {
          inWrapper: true,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
          experimental: true,
        });
      })
      .catch(() => {
        if (!cancelled) setDocxError('This Word document could not be rendered.');
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, loadState, blob, activeUrl]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !active) return null;

  const fileName = getFileName(active.url);
  const previewKind = getPreviewKind(active.url);
  const canAct = loadState === 'ready' && !!objectUrl;

  const renderBody = () => {
    if (loadState === 'loading') {
      return (
        <div className="h-full flex flex-col items-center justify-center text-center p-8">
          <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-indigo-600 animate-spin" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">Loading document…</p>
        </div>
      );
    }

    if (loadState === 'error') {
      return (
        <div className="h-full flex flex-col items-center justify-center text-center p-8">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-500 mb-3" />
          <p className="text-gray-900 dark:text-gray-100 font-medium">Couldn’t open this document</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
            <span className="font-medium">{fileName}</span> — {errorMessage}
          </p>
        </div>
      );
    }

    if (previewKind === 'docx' && !docxError) {
      return (
        <div className="h-full overflow-auto bg-gray-200 dark:bg-gray-900">
          <div ref={docxContainerRef} className="docx-host" />
        </div>
      );
    }

    if (previewKind === 'unsupported' || docxError) {
      return (
        <div className="h-full flex flex-col items-center justify-center text-center p-8">
          <ExclamationTriangleIcon className="h-10 w-10 text-amber-500 mb-3" />
          <p className="text-gray-900 dark:text-gray-100 font-medium">
            {docxError ? 'This document couldn’t be rendered' : 'This format can’t be previewed in the browser'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
            <span className="font-medium">{fileName}</span> — download it or open it in a new tab
            to read it.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <a
              href={objectUrl}
              download={fileName}
              className="inline-flex items-center px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Download
            </a>
            <a
              href={objectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
              Open in new tab
            </a>
          </div>
        </div>
      );
    }

    if (previewKind === 'image') {
      return (
        <div className="h-full overflow-auto flex items-center justify-center p-4">
          <img src={objectUrl} alt={`${active.label} preview`} className="max-w-full object-contain" />
        </div>
      );
    }

    return (
      <iframe
        key={active.id}
        src={objectUrl}
        title={`${active.label} preview`}
        className="w-full h-full border-0"
      />
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${active.label} preview`}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
              {active.label}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {subtitle ? `${subtitle} · ${fileName}` : fileName}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canAct && (
              <>
                <a
                  href={objectUrl}
                  download={fileName}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Download"
                  aria-label="Download document"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </a>
                <a
                  href={objectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Open in new tab"
                  aria-label="Open document in a new tab"
                >
                  <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </a>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
              aria-label="Close document viewer"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Document switcher — only meaningful with more than one document */}
        {documents.length > 1 && (
          <div
            className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto"
            role="tablist"
            aria-label="Candidate documents"
          >
            {documents.map((doc) => {
              const isActive = doc.id === active.id;
              return (
                <button
                  key={doc.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveId(doc.id)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {doc.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Preview */}
        <div className="flex-1 min-h-0 bg-gray-100 dark:bg-gray-900">{renderBody()}</div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center min-w-0">
            <DocumentTextIcon className="h-4 w-4 mr-1.5 shrink-0" />
            <span className="truncate">
              {documents.length > 1
                ? `Document ${documents.findIndex((doc) => doc.id === active.id) + 1} of ${documents.length}`
                : fileName}
            </span>
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
          >
            Back to application
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewerModal;
